import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, FileText, Calendar, Shield, Scale, ChevronDown, MessageSquare, PenTool, Zap, Gavel } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, isRTL, dir } = useLanguage();
  const [showUrgentMenu, setShowUrgentMenu] = useState(false);
  const urgentMenuRef = useRef(null);

  // إغلاق القائمة عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (urgentMenuRef.current && !urgentMenuRef.current.contains(event.target)) {
        setShowUrgentMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-sand" dir={dir}>
      {/* Header */}
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">
              {t('landing.heroTitle')}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSelector variant="minimal" className="text-white" />
            <button
              data-testid="header-login-btn"
              onClick={() => navigate('/login')}
              className="px-6 py-2 text-white border border-white/30 rounded-md hover:bg-white hover:text-primary transition-all duration-300"
            >
              {t('nav.login')}
            </button>
            <button
              data-testid="header-register-btn"
              onClick={() => navigate('/register')}
              className="px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300 shadow-md"
            >
              {t('nav.register')}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative py-24 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.85)), url('https://images.unsplash.com/photo-1623428997047-296a86d28a20?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6 animate-fade-in-up">
            {t('landing.expertLawyers')}
            <span className="block text-gold mt-2">{t('landing.heroSubtitle')}</span>
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed font-body">
            {t('landing.comprehensiveServices')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* قائمة طلب خدمة مستعجلة */}
            <div className="relative" ref={urgentMenuRef}>
              <button
                data-testid="hero-urgent-service-btn"
                onClick={() => setShowUrgentMenu(!showUrgentMenu)}
                className="px-12 py-4 bg-red-600 border-4 border-gold text-white font-bold rounded-md hover:bg-red-700 transition-all duration-300 shadow-xl flex flex-col items-center justify-center gap-1 ring-2 ring-gold/50 ring-offset-2"
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  <span className="text-lg">{t('landing.urgentService')}</span>
                </div>
                <div className="flex items-center gap-2 text-base">
                  <span>{t('landing.urgentConsultation')} | {t('landing.notaryServices')}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showUrgentMenu ? 'rotate-180' : ''}`} />
                </div>
              </button>
              
              {showUrgentMenu && (
                <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-2xl border border-gray-100 overflow-hidden z-50">
                  <button
                    data-testid="urgent-consultation-btn"
                    onClick={() => navigate('/guest-consultation')}
                    className="w-full flex items-center gap-3 px-6 py-4 text-primary hover:bg-gold/10 transition-colors border-b border-gray-100"
                  >
                    <div className="p-2 bg-gold/20 rounded-lg">
                      <MessageSquare className="w-5 h-5 text-gold" />
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <span className="font-bold block">{t('landing.urgentConsultation')}</span>
                      <span className="text-sm text-gray-500">{t('landing.legalConsultingDesc')}</span>
                    </div>
                  </button>
                  <button
                    data-testid="urgent-notary-btn"
                    onClick={() => navigate('/guest-notary')}
                    className="w-full flex items-center gap-3 px-6 py-4 text-primary hover:bg-purple-50 transition-colors"
                  >
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <PenTool className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <span className="font-bold block">{t('landing.notaryServices')}</span>
                      <span className="text-sm text-gray-500">{t('landing.realEstateDesc')}</span>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* زر المحكمة الافتراضية */}
            <button
              data-testid="virtual-court-btn"
              onClick={() => navigate('/virtual-court')}
              className="px-8 py-4 bg-gradient-to-r from-blue-900 to-indigo-900 border-2 border-gold text-white font-bold rounded-md hover:from-blue-800 hover:to-indigo-800 transition-all duration-300 shadow-xl flex flex-col items-center justify-center gap-1"
            >
              <div className="flex items-center gap-2">
                <Gavel className="w-5 h-5 text-gold" />
                <span className="text-lg">{t('nav.virtualCourt')}</span>
              </div>
              <span className="text-sm text-white/70">{t('landing.virtualCourtDesc')}</span>
            </button>
          </div>
        </div>
      </section>

      {/* قسم المحكمة الافتراضية المميز */}
      <section className="py-12 bg-gradient-to-r from-primary via-primary-800 to-primary relative overflow-hidden">
        {/* خلفية مزخرفة */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            {/* المحتوى */}
            <div className={`flex-1 text-center ${isRTL ? 'lg:text-right' : 'lg:text-left'}`}>
              <div className="inline-flex items-center gap-2 bg-gold/20 px-4 py-2 rounded-full mb-4">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-gold text-sm font-bold">{t('landing.newAvailable')}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                {t('nav.virtualCourt')}
              </h2>
              <p className="text-white/80 text-lg mb-6 max-w-xl">
                {t('landing.virtualCourtFullDesc')}
              </p>
              <div className={`flex flex-wrap gap-4 justify-center ${isRTL ? 'lg:justify-start' : 'lg:justify-start'} mb-6`}>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                  <Scale className="w-5 h-5 text-gold" />
                  <span className="text-white text-sm">{t('landing.realisticSimulation')}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                  <Users className="w-5 h-5 text-gold" />
                  <span className="text-white text-sm">{t('landing.judicialRoles')}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                  <FileText className="w-5 h-5 text-gold" />
                  <span className="text-white text-sm">{t('landing.realCases')}</span>
                </div>
              </div>
              <button
                data-testid="virtual-court-btn"
                onClick={() => navigate('/virtual-court')}
                className="group px-10 py-4 bg-gold text-primary text-xl font-bold rounded-xl hover:bg-white transition-all duration-300 shadow-2xl hover:shadow-gold/50 flex items-center gap-3 mx-auto lg:mx-0"
              >
                <Gavel className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                ادخل المحكمة الآن
                <span className="text-sm bg-primary/20 px-2 py-1 rounded">مجاني</span>
              </button>
            </div>
            
            {/* الأيقونة الكبيرة */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-48 h-48 md:w-64 md:h-64 bg-gradient-to-br from-gold to-yellow-600 rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-105 transition-transform cursor-pointer" onClick={() => navigate('/virtual-court')}>
                  <Gavel className="w-24 h-24 md:w-32 md:h-32 text-white" />
                </div>
                {/* شارة */}
                <div className="absolute -top-4 -right-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold animate-bounce">
                  🎮 ألعاب تفاعلية
                </div>
                {/* إحصائيات */}
                <div className="absolute -bottom-4 -left-4 bg-white text-primary px-4 py-2 rounded-xl shadow-lg">
                  <div className="text-2xl font-bold">+5</div>
                  <div className="text-xs text-gray-500">قضايا تدريبية</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Tetris Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl md:text-5xl font-heading font-bold text-primary text-center mb-16">
            خدماتنا القانونية
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 glass-card rounded-xl p-8 hover-lift" data-testid="service-cases">
              <FileText className="w-12 h-12 text-gold mb-4" />
              <h4 className="text-2xl font-heading font-semibold text-primary mb-3">
                إدارة القضايا
              </h4>
              <p className="text-base leading-relaxed text-gray-700 font-body">
                نظام متكامل لإدارة القضايا القانونية بكفاءة عالية، مع متابعة دقيقة
                لجميع التفاصيل والمواعيد القضائية.
              </p>
            </div>

            <div className="glass-card rounded-xl p-8 hover-lift" data-testid="service-consultation">
              <Users className="w-12 h-12 text-gold mb-4" />
              <h4 className="text-2xl font-heading font-semibold text-primary mb-3">
                استشارات عن بعد
              </h4>
              <p className="text-base leading-relaxed text-gray-700 font-body">
                احصل على استشارات قانونية احترافية من منزلك عبر منصتنا الإلكترونية.
              </p>
            </div>

            <div className="glass-card rounded-xl p-8 hover-lift" data-testid="service-appointments">
              <Calendar className="w-12 h-12 text-gold mb-4" />
              <h4 className="text-2xl font-heading font-semibold text-primary mb-3">
                إدارة المواعيد
              </h4>
              <p className="text-base leading-relaxed text-gray-700 font-body">
                جدولة مواعيدك بسهولة ومرونة تامة مع إمكانية المتابعة المستمرة.
              </p>
            </div>

            <div className="md:col-span-2 glass-card rounded-xl p-8 hover-lift" data-testid="service-documents">
              <Shield className="w-12 h-12 text-gold mb-4" />
              <h4 className="text-2xl font-heading font-semibold text-primary mb-3">
                حفظ المستندات الآمن
              </h4>
              <p className="text-base leading-relaxed text-gray-700 font-body">
                نحفظ جميع مستنداتك القانونية بأعلى معايير الأمان والخصوصية، مع
                إمكانية الوصول إليها في أي وقت.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl md:text-5xl font-heading font-bold text-primary text-center mb-16">
            لماذا تختار مكتبنا؟
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center" data-testid="why-experience">
              <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-heading font-semibold text-primary mb-3">
                خبرة واسعة
              </h4>
              <p className="text-base leading-relaxed text-gray-700 font-body">
                سنوات من الخبرة في مختلف المجالات القانونية
              </p>
            </div>

            <div className="text-center" data-testid="why-professionalism">
              <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-6">
                <Scale className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-heading font-semibold text-primary mb-3">
                احترافية عالية
              </h4>
              <p className="text-base leading-relaxed text-gray-700 font-body">
                نلتزم بأعلى معايير المهنية والأخلاقيات القانونية
              </p>
            </div>

            <div className="text-center" data-testid="why-support">
              <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-heading font-semibold text-primary mb-3">
                دعم متواصل
              </h4>
              <p className="text-base leading-relaxed text-gray-700 font-body">
                نحن معك في كل خطوة حتى تحقيق النتيجة المرجوة
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6">
            هل تحتاج إلى مساعدة قانونية؟
          </h3>
          <p className="text-lg text-white/90 mb-10 font-body">
            تواصل معنا اليوم واحصل على استشارة قانونية متخصصة
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="footer-guest-consultation-btn"
              onClick={() => navigate('/guest-consultation')}
              className="px-12 py-4 bg-gold text-white text-lg font-bold rounded-md hover:bg-gold-dark transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1"
            >
              طلب استشارة فورية
            </button>
            <button
              data-testid="footer-register-btn"
              onClick={() => navigate('/register')}
              className="px-12 py-4 bg-transparent border-2 border-white text-white text-lg font-bold rounded-md hover:bg-white hover:text-primary transition-all duration-300 shadow-xl"
            >
              إنشاء حساب
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Logo size="sm" />
            <p className="text-lg font-heading font-semibold">
              مجموعة المحامي هشام يوسف الخياط
            </p>
          </div>
          <p className="text-sm text-white/70 font-body">
            جميع الحقوق محفوظة © 2025
          </p>
        </div>
      </footer>
    </div>
  );
}
